import React, { useState } from 'react';
import { View, Text, StyleSheet, ViewStyle, TextStyle, TouchableOpacity } from 'react-native';

import AppTextInput from './AppTextInput';
interface ModernInputProps {
  placeholder?: string;
  value: string;
  onChangeText: (text: string) => void;
  label?: string;
  error?: string;
  success?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  secureTextEntry?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  style?: ViewStyle;
  inputStyle?: TextStyle;
  disabled?: boolean;
  keyboardType?: 'default' | 'email-address' | 'numeric' | 'phone-pad';
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  autoCorrect?: boolean;
  autoComplete?: 'off' | 'name' | 'email' | 'tel' | 'street-address' | 'none' | 'new-password' | 'current-password';
  textContentType?: 'none' | 'username' | 'password' | 'newPassword' | 'oneTimeCode';
}

const ModernInput: React.FC<ModernInputProps> = ({
  placeholder,
  value,
  onChangeText,
  label,
  error,
  success,
  leftIcon,
  rightIcon,
  secureTextEntry = false,
  multiline = false,
  numberOfLines = 1,
  style,
  inputStyle,
  disabled = false,
  keyboardType = 'default',
  autoCapitalize = 'sentences',
  autoCorrect = true,
  autoComplete,
  textContentType,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getContainerStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      borderWidth: 2,
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: '#FFFFFF',
      flexDirection: 'row',
      alignItems: multiline ? 'flex-start' : 'center',
      minHeight: multiline ? 80 : 44,
    };

    if (disabled) {
      return {
        ...baseStyle,
        backgroundColor: '#F2F2F7',
        borderColor: '#E1E5E9',
      };
    }

    if (error) {
      return {
        ...baseStyle,
        borderColor: '#FF3B30',
      };
    }

    if (success) {
      return {
        ...baseStyle,
        borderColor: '#34C759',
      };
    }

    if (isFocused) {
      return {
        ...baseStyle,
        borderColor: '#007AFF',
        shadowColor: '#007AFF',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 2,
      };
    }

    return {
      ...baseStyle,
      borderColor: '#E1E5E9',
    };
  };

  const getTextInputStyle = (): TextStyle => {
    return {
      flex: 1,
      fontSize: 16,
      color: disabled ? '#8E8E93' : '#1C1C1E',
      textAlignVertical: multiline ? 'top' : 'center',
      ...inputStyle,
    };
  };

  return (
    <View style={style}>
      {label && (
        <Text style={styles.label}>{label}</Text>
      )}
      
      <View style={getContainerStyle()}>
        {leftIcon && (
          <View style={styles.leftIcon}>
            {leftIcon}
          </View>
        )}
        
        <AppTextInput
          style={getTextInputStyle()}
          placeholder={placeholder}
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          secureTextEntry={secureTextEntry}
          multiline={multiline}
          numberOfLines={numberOfLines}
          editable={!disabled}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          autoComplete={autoComplete}
          textContentType={textContentType}
        />
        
        {rightIcon && (
          <View style={styles.rightIcon}>
            {rightIcon}
          </View>
        )}
      </View>
      
      {error && (
        <Text style={styles.errorText}>{error}</Text>
      )}
      
      {success && !error && (
        <Text style={styles.successText}>✓ Valid</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  leftIcon: {
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightIcon: {
    marginLeft: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#FF3B30',
    marginTop: 4,
  },
  successText: {
    fontSize: 14,
    color: '#34C759',
    marginTop: 4,
  },
});

export default ModernInput;